(() => {
  const slugify = (text) =>
    text
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80) || 'section';

  /**
   * Blog CMS injects .ray-article + inline styles that beat theme typography.
   * - Media: keep size/layout (width/height/…); strip font/color on media.
   * - Text: keep color / background-color from the editor; strip the rest.
   */
  const MEDIA_TAGS = new Set(['IMG', 'VIDEO', 'IFRAME', 'SVG', 'SOURCE', 'PICTURE']);
  /* Keep size; drop float/clear — CMS float:left collapses vertical gap + left-aligns small images */
  const MEDIA_STRIP_PROPS = /^(font|line-height|letter-spacing|color|text-|float|clear)/i;
  const TEXT_KEEP_PROPS = /^(color|background-color|background)$/i;

  const filterStyleAttr = (styleValue, keepPred) => {
    const next = [];
    String(styleValue || '')
      .split(';')
      .forEach((part) => {
        const decl = part.trim();
        if (!decl) return;
        const prop = decl.split(':')[0].trim();
        if (prop && keepPred(prop)) next.push(decl);
      });
    return next;
  };

  const sanitizeCmsStyles = (article) => {
    if (!article || article.dataset.saBpcSanitized === '1') return;
    article.dataset.saBpcSanitized = '1';

    article.querySelectorAll('style').forEach((el) => el.remove());

    article.querySelectorAll('[style]').forEach((el) => {
      if (MEDIA_TAGS.has(el.tagName)) {
        const next = filterStyleAttr(
          el.getAttribute('style'),
          (prop) => !MEDIA_STRIP_PROPS.test(prop)
        );
        if (next.length) el.setAttribute('style', next.join('; '));
        else el.removeAttribute('style');
        return;
      }
      const kept = filterStyleAttr(el.getAttribute('style'), (prop) =>
        TEXT_KEEP_PROPS.test(prop)
      );
      if (kept.length) el.setAttribute('style', kept.join('; '));
      else el.removeAttribute('style');
    });
  };

  /** True when a <p> is only a bold question (optional trailing <br>). */
  const isStrongOnlyQuestion = (el) => {
    if (!el || el.tagName !== 'P') return false;
    const kids = [...el.childNodes].filter((n) => {
      if (n.nodeType === Node.TEXT_NODE) return n.textContent.trim().length > 0;
      if (n.nodeType === Node.ELEMENT_NODE) return n.tagName !== 'BR';
      return false;
    });
    if (kids.length !== 1) return false;
    const only = kids[0];
    return (
      only.nodeType === Node.ELEMENT_NODE &&
      (only.tagName === 'STRONG' || only.tagName === 'B') &&
      /\?[\s»"”']*$/.test((only.textContent || '').trim())
    );
  };

  /**
   * Normalize FAQ blocks after an H2 whose title/id looks like FAQ.
   * Marks .sa-bpc__faq-q / .sa-bpc__faq-a so CSS can match editor variants:
   *   h3+p | p>strong + p | existing .faq-answer
   */
  const normalizeFaqBlocks = (article) => {
    if (!article || article.dataset.saBpcFaqNorm === '1') return;
    article.dataset.saBpcFaqNorm = '1';

    [...article.querySelectorAll('h2')].forEach((h2) => {
      const label = `${h2.id || ''} ${h2.textContent || ''}`;
      if (!/faq/i.test(label)) return;

      let el = h2.nextElementSibling;
      while (el && el.tagName !== 'H2') {
        const next = el.nextElementSibling;

        if (el.classList.contains('faq-answer')) {
          el.classList.add('sa-bpc__faq-a');
          el = next;
          continue;
        }

        if (el.tagName === 'H3' && next && next.tagName === 'P') {
          el.classList.add('sa-bpc__faq-q');
          next.classList.add('sa-bpc__faq-a');
          el = next.nextElementSibling;
          continue;
        }

        if (el.tagName === 'P' && next) {
          if (next.classList.contains('faq-answer')) {
            el.classList.add('sa-bpc__faq-q');
            next.classList.add('sa-bpc__faq-a');
            el = next.nextElementSibling;
            continue;
          }
          if (isStrongOnlyQuestion(el) && next.tagName === 'P') {
            el.classList.add('sa-bpc__faq-q');
            next.classList.add('sa-bpc__faq-a');
            el = next.nextElementSibling;
            continue;
          }
          /* Plain <p>Q?</p><p>A</p> (no strong) */
          if (
            next.tagName === 'P' &&
            !next.querySelector('img, table, iframe, video') &&
            /\?[\s»"”']*$/.test((el.textContent || '').trim()) &&
            (el.textContent || '').trim().length < 180
          ) {
            el.classList.add('sa-bpc__faq-q');
            next.classList.add('sa-bpc__faq-a');
            el = next.nextElementSibling;
            continue;
          }
        }

        el = next;
      }
    });
  };

  const initBlock = (root) => {
    if (!root || root.dataset.saBpcInit === '1') return;
    root.dataset.saBpcInit = '1';

    const article = root.querySelector('[data-sa-bpc-article]');
    const toc = root.querySelector('[data-sa-bpc-toc]');
    const panel = root.querySelector('[data-sa-bpc-toc-panel]');
    const list = root.querySelector('[data-sa-bpc-toc-list]');
    const toggle = root.querySelector('[data-sa-bpc-toc-toggle]');
    if (!article || !toc || !panel || !list || !toggle) return;

    sanitizeCmsStyles(article);
    normalizeFaqBlocks(article);
    article.classList.remove('sa-bpc__article--pending');

    const mqDesktop = window.matchMedia('(min-width: 1000px)');
    const TOC_VISIBLE_MAX = 5;
    const headings = [...article.querySelectorAll('h2')];
    const moreBtn = root.querySelector('[data-sa-bpc-toc-more]');
    let userToggled = false;
    let listExpanded = false;

    const setOpen = (open) => {
      toc.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        panel.hidden = false;
        panel.removeAttribute('hidden');
      } else {
        panel.hidden = true;
      }
    };

    const applyDefaultOpen = () => {
      if (userToggled) return;
      setOpen(mqDesktop.matches);
    };

    toggle.addEventListener('click', () => {
      userToggled = true;
      setOpen(!toc.classList.contains('is-open'));
    });

    list.innerHTML = '';
    if (!headings.length) {
      toc.hidden = true;
      return;
    }

    const usedIds = new Set();
    headings.forEach((h2, index) => {
      const title = h2.textContent.trim();
      if (!title) return;

      let baseId = slugify(title);
      if (!baseId) baseId = `section-${index + 1}`;
      let id = baseId;
      let n = 2;
      while (usedIds.has(id) || document.getElementById(id)) {
        id = `${baseId}-${n}`;
        n += 1;
      }
      usedIds.add(id);
      h2.id = id;
      h2.classList.add('sa-bpc__h2-section');

      const li = document.createElement('li');
      li.className = 'sa-bpc__toc-item';
      li.dataset.saBpcTocItem = String(index);

      const a = document.createElement('a');
      a.className = 'sa-bpc__toc-link';
      a.href = `#${id}`;
      /* Figma: 01. / 02. — two-digit + dot; weight via .sa-bpc__toc-num */
      const num = String(index + 1).padStart(2, '0');
      a.innerHTML = `<span class="sa-bpc__toc-num">${num}.</span><span class="sa-bpc__toc-text"></span>`;
      a.querySelector('.sa-bpc__toc-text').textContent = title;

      a.addEventListener('click', (e) => {
        e.preventDefault();
        const styles = getComputedStyle(document.documentElement);
        const headerH = parseFloat(
          styles.getPropertyValue('--header-height') ||
            styles.getPropertyValue(mqDesktop.matches ? '--sa-header-desktop-height' : '--sa-header-mobile-height') ||
            (mqDesktop.matches ? '60' : '44')
        );
        const gap = mqDesktop.matches ? 16 : 12;

        const scrollToHeading = () => {
          const stickyTop = mqDesktop.matches
            ? headerH
            : parseFloat(styles.getPropertyValue('--sa-header-bottom-offset')) || headerH;
          const tocH = mqDesktop.matches ? 0 : toc.getBoundingClientRect().height;
          const top = h2.getBoundingClientRect().top + window.scrollY - stickyTop - tocH - gap;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        };

        /* Mobile: TOC collapses on click. Measure AFTER close or scroll overshoots
           (target was computed while expanded panel still pushed headings down). PC unchanged. */
        if (!mqDesktop.matches) {
          userToggled = true;
          if (toc.classList.contains('is-open')) {
            setOpen(false);
            requestAnimationFrame(() => {
              requestAnimationFrame(scrollToHeading);
            });
            return;
          }
        }

        scrollToHeading();
      });

      li.appendChild(a);
      list.appendChild(li);
    });

    const tocItems = [...list.querySelectorAll('.sa-bpc__toc-item')];
    tocItems.forEach((li, index) => {
      if (index >= TOC_VISIBLE_MAX) li.classList.add('sa-bpc__toc-item--extra');
    });

    const applyListTruncation = () => {
      const hasOverflow = tocItems.length > TOC_VISIBLE_MAX;
      const needTruncate = hasOverflow && !listExpanded;
      toc.classList.toggle('sa-bpc__toc--truncated', needTruncate);
      if (moreBtn) {
        moreBtn.hidden = !hasOverflow;
        if (hasOverflow) {
          const moreLabel = toc.getAttribute('data-sa-bpc-toc-more-label');
          const lessLabel = toc.getAttribute('data-sa-bpc-toc-less-label');
          const labelEl = moreBtn.querySelector('[data-sa-bpc-toc-more-text]');
          if (labelEl) labelEl.textContent = listExpanded ? lessLabel || 'Show less' : moreLabel || 'Show more';
          moreBtn.classList.toggle('is-expanded', listExpanded);
          moreBtn.setAttribute('aria-expanded', listExpanded ? 'true' : 'false');
        }
      }
    };

    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        listExpanded = !listExpanded;
        applyListTruncation();
      });
    }

    /* No default / scroll “active” highlight — only :hover (per design) */

    applyDefaultOpen();
    applyListTruncation();
    mqDesktop.addEventListener('change', () => {
      userToggled = false;
      listExpanded = false;
      applyDefaultOpen();
      applyListTruncation();
    });
  };

  document.querySelectorAll('[data-sa-bpc-root]').forEach(initBlock);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-sa-bpc-root]');
    if (root) {
      delete root.dataset.saBpcInit;
      const article = root.querySelector('[data-sa-bpc-article]');
      if (article) delete article.dataset.saBpcSanitized;
      initBlock(root);
    }
  });
})();
